import { getBrowser, getOS } from '@proton/shared/lib/helpers/browser';

import { MIME_TYPES } from '../constants';
import type { DocsConversionType } from '../docs/constants';
import {
    RAWMimeTypes,
    RAWThumbnailExtractionSupported,
    SupportedMimeTypes,
    SupportedProtonDocsMimeTypes,
} from '../drive/constants';
import { Version } from './version';

const isWebpSupported = () => {
    const { name, version } = getBrowser();

    if (name === 'Safari') {
        /*
         * The support for WebP image format became available in Safari 14.
         * It is not possible to support webp images in older Safari versions.
         * https://developer.apple.com/documentation/safari-release-notes/safari-14-release-notes
         */
        return Number(version?.split('.')[0]) >= 14;
    }

    return true;
};

export const isHEICSupported = () => {
    const os = getOS();
    const { name, version } = getBrowser();
    return Boolean(
        ['mac os', 'ios'].includes(os.name.toLowerCase()) &&
        ['Safari', 'Mobile Safari'].includes(name || '') &&
        version &&
        new Version(version).isGreaterThanOrEqual('17')
    );
};

const isJXLSupported = () => {
    const os = getOS();
    const { name, version } = getBrowser();
    return (
        ['mac os', 'ios'].includes(os.name.toLowerCase()) &&
        ['Safari', 'Mobile Safari'].includes(name || '') &&
        version &&
        new Version(version).isGreaterThanOrEqual('17')
    );
};

export const isImage = (mimeType: string) => mimeType.startsWith('image/');

export const isExcel = (mimeType: string) => mimeType.startsWith('application/vnd.ms-excel');

export const isXlsx = (mimeType: string) => mimeType === SupportedMimeTypes.xlsx;

export const isWordDocument = (mimeType: string) => mimeType === SupportedMimeTypes.docx;

export const isFont = (mimeType: string) => mimeType.startsWith('font/');

export const isSupportedImage = (mimeType: string) =>
    [
        SupportedMimeTypes.apng,
        SupportedMimeTypes.bmp,
        SupportedMimeTypes.gif,
        SupportedMimeTypes.ico,
        SupportedMimeTypes.vdnMicrosoftIcon,
        SupportedMimeTypes.jpg,
        'image/jpg', // Support also wrongly labeled JPG files
        SupportedMimeTypes.png,
        SupportedMimeTypes.svg,
        isWebpSupported() && SupportedMimeTypes.webp,
        SupportedMimeTypes.avif,
        isHEICSupported() && SupportedMimeTypes.heic,
        isJXLSupported() && SupportedMimeTypes.jxl,
    ]
        .filter(Boolean)
        .includes(mimeType as SupportedMimeTypes);

export const isSVG = (mimeType: string) => mimeType === SupportedMimeTypes.svg;

export const isHEIC = (mimeType: string) =>
    mimeType === SupportedMimeTypes.heic || mimeType === SupportedMimeTypes.heif;

export const isICS = (mimeType: string) =>
    mimeType.startsWith(MIME_TYPES.ICS) || mimeType.includes(MIME_TYPES.APPLICATION_ICS);

export const isSupportedText = (mimeType: string) =>
    mimeType.startsWith('text/') ||
    [
        'application/json',
        'application/javascript',
        'application/typescript',
        'application/x-tex',
        'application/x-csh',
        'application/x-sh',
        'application/x-httpd-php',
        'application/xhtml+xml',
    ].includes(mimeType);
export const isVideo = (mimeType: string) => mimeType.startsWith('video/');
export const isAudio = (mimeType: string) => mimeType.startsWith('audio/');
export const isPDF = (mimeType: string) =>
    mimeType === SupportedMimeTypes.pdf ||
    // Some e-mail clients will use this legacy value
    mimeType === 'application/x-pdf';
export const isComicBook = (mimeType: string) =>
    mimeType === 'application/x-cbz' ||
    mimeType === 'application/x-cbr' ||
    mimeType === 'application/vnd.comicbook+zip';
export const isCompatibleCBZ = (mimeType: string, filename: string) => {
    const isCBZFile = filename.endsWith('cbz');
    // browser mime type detection is not great for CBZ (sometimes flagged as 'application/x-cbr') so we need to also check end of file name
    return (isComicBook(mimeType) && isCBZFile) || (mimeType === 'application/zip' && isCBZFile);
};

/**
 * Whether a given mimetype can be converted to a Proton Docs document.
 */
export const isConvertibleToProtonDocsDocument = (mimeType: string) =>
    mimeType === SupportedProtonDocsMimeTypes.docx ||
    mimeType === SupportedProtonDocsMimeTypes.txt ||
    mimeType === SupportedProtonDocsMimeTypes.md ||
    mimeType === SupportedProtonDocsMimeTypes.html;
/**
 * Whether a given mimetype can be converted to a Proton Sheets spreadsheet.
 */
export const isConvertibleToProtonDocsSpreadsheet = (mimeType: string) =>
    mimeType === SupportedProtonDocsMimeTypes.xlsx ||
    mimeType === SupportedProtonDocsMimeTypes.csv ||
    mimeType === SupportedProtonDocsMimeTypes.tsv ||
    mimeType === SupportedProtonDocsMimeTypes.ods;

export const getDocsConversionType = (mimeType: string): DocsConversionType => {
    switch (mimeType) {
        case SupportedProtonDocsMimeTypes.xlsx:
            return 'xlsx';
        case SupportedProtonDocsMimeTypes.ods:
            return 'ods';
        case SupportedProtonDocsMimeTypes.csv:
            return 'csv';
        case SupportedProtonDocsMimeTypes.tsv:
            return 'tsv';
        case SupportedProtonDocsMimeTypes.docx:
            return 'docx';
        case SupportedProtonDocsMimeTypes.txt:
            return 'txt';
        case SupportedProtonDocsMimeTypes.html:
            return 'html';
        default:
            return 'md';
    }
};

export const PROTON_DOCS_DOCUMENT_MIMETYPE = 'application/vnd.proton.doc';
export const isProtonDocsDocument = (mimeType: string) => mimeType === PROTON_DOCS_DOCUMENT_MIMETYPE;
export const PROTON_DOCS_SPREADSHEET_MIMETYPE = 'application/vnd.proton.sheet';
export const isProtonDocsSpreadsheet = (mimeType: string) => mimeType === PROTON_DOCS_SPREADSHEET_MIMETYPE;
export const isNativeProtonDocsAppFile = (mimeType: string): boolean => {
    return isProtonDocsDocument(mimeType) || isProtonDocsSpreadsheet(mimeType);
};

export type ProtonDocumentType = 'document' | 'spreadsheet';

export type OpenInDocsType = { type: ProtonDocumentType; isNative: boolean };

export function mimeTypeToOpenInDocsType(mimeType?: string, isODSImportEnabled?: boolean): OpenInDocsType | undefined {
    if (!mimeType) {
        return undefined;
    }

    const isDocument = isProtonDocsDocument(mimeType);
    const isSheet = isProtonDocsSpreadsheet(mimeType);
    const isConvertibleToSpreadsheet = isConvertibleToProtonDocsSpreadsheet(mimeType);
    const isConvertibleToDocument = isConvertibleToProtonDocsDocument(mimeType);

    const isNative = isDocument || isSheet;

    if (isDocument || isConvertibleToDocument) {
        return { type: 'document', isNative };
    }
    if (isSheet || isConvertibleToSpreadsheet) {
        if (!isODSImportEnabled && mimeType === SupportedProtonDocsMimeTypes.ods) {
            return undefined;
        }
        return { type: 'spreadsheet', isNative };
    }
    return undefined;
}

export function mimeTypeToProtonDocumentType(mimeType?: string): ProtonDocumentType | undefined {
    if (!mimeType) {
        return;
    }
    if (isProtonDocsDocument(mimeType)) {
        return 'document';
    }
    if (isProtonDocsSpreadsheet(mimeType)) {
        return 'spreadsheet';
    }
}

export const isSTLFile = (mimeType: string) => mimeType === 'model/stl';
export const isCompatibleSTL = (mimeType: string, filename: string) => isSTLFile(mimeType) && filename.endsWith('stl'); // browser mime type detection is not great for STL so we need to also check end of file name

export const isRAWPhoto = (mimeType: string): boolean => {
    return Object.values(RAWMimeTypes).some((rawType) => rawType === mimeType);
};

export const isRAWExtension = (extension: string | undefined): boolean => {
    if (!extension) {
        return false;
    }

    const lowerExt = extension.toLowerCase();
    return Object.keys(RAWMimeTypes).includes(lowerExt);
};

export const getFileExtension = (name: string | undefined) => {
    return (name || '').split('.').pop();
};

export const getRAWMimeTypeFromName = (name: string): string | undefined => {
    if (!name) {
        return undefined;
    }

    const extension = getFileExtension(name);
    const lowerExt = (extension || '').toLowerCase();
    const matchingKey = Object.keys(RAWMimeTypes).find((key) => lowerExt === key);
    return matchingKey ? RAWMimeTypes[matchingKey as keyof typeof RAWMimeTypes] : undefined;
};

export const isRAWThumbnailExtractionSupported = (mimeType: string, extension: string | undefined): boolean => {
    if (!extension) {
        return false;
    }

    const keys = Object.keys(RAWThumbnailExtractionSupported);
    const values = Object.values(RAWThumbnailExtractionSupported);
    const lowerExt = extension.toLowerCase();

    return keys.includes(lowerExt) || values.some((rawType) => rawType === mimeType);
};

export const isIWAD = (mimeType: string): boolean =>
    ['application/x-doom', 'application/x-doom-wad'].includes(mimeType);

/**
 * Returns true for image formats that can be rendered natively by browsers
 * and support direct thumbnail generation without conversion.
 * Excludes formats that require specialized handlers (SVG, RAW).
 * HEIC is included on Safari 17+ (macOS/iOS) where it's natively supported.
 */
export const isNativeSupportedImage = (mimeType: string) => {
    if (isSVG(mimeType) || isRAWPhoto(mimeType)) {
        return false;
    }
    if (isHEIC(mimeType)) {
        return isHEICSupported();
    }
    return isSupportedImage(mimeType);
};
